import { useMemo, useState } from 'react';
import { EDetectionRefVariant, IDetectedReference, IDetectedSelfReference } from '../types/references.types';
import { groupBySource, mapToObject } from '../helpers/group.helper';

interface UseGroupedOpts {
  /** Optional filter (e.g. r => r.inserted) */
  filter?: (r: IDetectedReference | IDetectedSelfReference) => boolean;
  // add more options if needed
}

/**
 * Groups refs/selfRefs once, then serves whichever tab is active.
 */
export const useGroupedRefs = (
  detectedReferences: readonly IDetectedReference[],
  detectedSelfReferences: readonly IDetectedSelfReference[],
  opts: UseGroupedOpts = {}
) => {
  const { filter } = opts;

  const groupedByType = useMemo(() => {
    const refs = filter ? detectedReferences.filter(filter) : detectedReferences;
    const selfRefs = filter ? detectedSelfReferences.filter(filter) : detectedSelfReferences;
    return {
      references: groupBySource(refs),
      selfReferences: groupBySource(selfRefs),
      referencesCount: refs.length,
      selfReferencesCount: selfRefs.length,
    };
  }, [detectedReferences, detectedSelfReferences, filter]);

  const [selectedTab, setSelectedTab] = useState<EDetectionRefVariant>(
    groupedByType.referencesCount === 0 ? EDetectionRefVariant.SELF : EDetectionRefVariant.EXTERNAL
  );

  const groupedRefs = useMemo(
    () =>
      mapToObject<IDetectedReference | IDetectedSelfReference>(
        selectedTab === EDetectionRefVariant.EXTERNAL ? groupedByType.references : groupedByType.selfReferences
      ),
    [selectedTab, groupedByType]
  );

  return { selectedTab, setSelectedTab, groupedRefs, groupedByType } as const;
};
